import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api, authState } from '../../state/auth.js';
import { aiAvailable, loadAiStatus, aiError } from '../../utils/ai.js';

// ✨ next to a text field: ask the club's AI for a proposal. The proposal is shown first;
// only "Gebruiken" puts it in the field, and the page saves it as always.
const ACTIONS = [
  { id: 'draft', label: 'Schrijf een voorstel', needsText: false },
  { id: 'improve', label: 'Verbeter', needsText: true },
  { id: 'shorter', label: 'Maak korter', needsText: true },
  { id: 'friendlier', label: 'Maak vriendelijker', needsText: true },
];

// Whether this member gets the AI helpers in the club that is active now
export function useAiAvailable() {
  const { userId, activeClubId } = authState.value;
  useEffect(() => { loadAiStatus(); }, [userId, activeClubId]);
  return aiAvailable.value.key === `${userId}:${activeClubId}` && aiAvailable.value.available;
}

export default function AiAssist({ kind, value = '', context = {}, onUse, extra = [] }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [error, setError] = useState(null);
  if (!useAiAvailable()) return null;

  const hasText = !!String(value || '').trim();
  async function ask(action) {
    setBusy(action); setError(null); setOpen(false);
    try {
      const { text } = await api('/api/ai-help/write', { method: 'POST', body: JSON.stringify({ kind, action, text: value || '', context }) });
      setProposal({ text, action });
    } catch (err) {
      setError(aiError(err));
    }
    setBusy(null);
  }

  const btn = 'border:1px solid var(--color-border);background:var(--color-bg-secondary);border-radius:6px;padding:3px 9px;font-size:12px;cursor:pointer;color:var(--color-text)';
  return (
    <div style="display:flex;flex-direction:column;gap:6px;margin-top:6px">
      <div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center">
        <button type="button" style={btn} aria-expanded={open} disabled={!!busy} onClick={() => setOpen(o => !o)}>
          {busy ? '✨ Bezig…' : '✨ Schrijfhulp'}
        </button>
        {open && [...ACTIONS, ...extra].filter(a => hasText || !a.needsText).map(a => (
          <button key={a.id} type="button" style={btn} onClick={() => ask(a.id)}>{a.label}</button>
        ))}
      </div>
      {error && <div class="text-sm" style="color:var(--color-danger,#b91c1c)">{error}</div>}
      {proposal && (
        <div style="border:1px dashed var(--color-border);border-radius:8px;padding:10px;background:var(--color-bg-secondary);display:flex;flex-direction:column;gap:8px">
          <div class="text-sm text-muted">Voorstel van de AI — controleer het voordat je het gebruikt</div>
          <div style="white-space:pre-wrap;font-size:14px;overflow-wrap:anywhere">{proposal.text}</div>
          <div style="display:flex;gap:6px;flex-wrap:wrap">
            <button type="button" class="btn btn-primary btn-sm" onClick={() => { onUse(proposal.text); setProposal(null); }}>Gebruiken</button>
            <button type="button" class="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => ask(proposal.action)}>Opnieuw</button>
            <button type="button" class="btn btn-secondary btn-sm" onClick={() => setProposal(null)}>Weggooien</button>
          </div>
        </div>
      )}
    </div>
  );
}
